import * as React from "react";
import { LogOut } from "lucide-react";
import { cn } from "@siena-ai/design-system";
import {
  getReaderFromCookie,
  signOutReader,
  type ReaderPrincipal,
} from "../reader-auth";

function getInitials(reader: ReaderPrincipal): string {
  const source = reader.name?.trim() || reader.email;
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  const cleaned = source.replace(/[^a-zA-Z0-9]/g, "");
  return (cleaned.slice(0, 2) || source.slice(0, 2)).toUpperCase();
}

interface ReaderMenuProps {
  className?: string;
}

/**
 * Avatar + dropdown shown on the public docs header whenever a reader JWT
 * cookie is present. Clicking opens a small menu with the reader's name /
 * email and a sign-out action. We intentionally avoid radix/headlessui — the
 * docs-site bundle is small on purpose, so we build a self-contained menu
 * with a click-outside + Escape handler.
 */
export const ReaderMenu: React.FC<ReaderMenuProps> = ({ className }) => {
  const [reader, setReader] = React.useState<ReaderPrincipal | null>(() =>
    getReaderFromCookie()
  );
  const [open, setOpen] = React.useState(false);
  const [signingOut, setSigningOut] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  // Re-check on mount (cookie may have been set by /sign-in/verify before we
  // got here) and whenever the tab regains focus.
  React.useEffect(() => {
    const refresh = () => setReader(getReaderFromCookie());
    refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!reader) return null;

  const displayName = reader.name?.trim() || reader.email;
  const initials = getInitials(reader);

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await signOutReader();
    } finally {
      // Force a full-document navigation that can never come from the
      // bfcache and can never resolve to the same URL as the current one
      // (which would let the browser short-circuit the reload). A unique
      // query string guarantees a fresh HTML + manifest fetch, so the
      // Cloudflare middleware re-evaluates the (now missing) cookie and
      // the SPA boots with empty state — showing the sign-in page for
      // logged_in spaces and the public homepage otherwise.
      if (typeof window !== "undefined") {
        window.location.replace(`/?signed_out=${Date.now()}`);
      }
    }
  };

  return (
    <div ref={menuRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${displayName}`}
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full",
          "bg-muted text-xs font-medium text-foreground",
          "hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2",
          "focus-visible:ring-ring focus-visible:ring-offset-1",
          "transition-colors"
        )}
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            "absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden",
            "rounded-md border border-border bg-popover p-1 shadow-md",
            "text-popover-foreground animate-in fade-in-0 zoom-in-95"
          )}
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium leading-none">
              {reader.name?.trim() || "Signed in"}
            </p>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {reader.email}
            </p>
          </div>
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            disabled={signingOut}
            className={cn(
              "flex w-full items-center gap-2 rounded-sm px-3 py-2",
              "text-sm text-foreground hover:bg-accent hover:text-accent-foreground",
              "focus:bg-accent focus:outline-none",
              "disabled:cursor-not-allowed disabled:opacity-60"
            )}
          >
            <LogOut className="h-4 w-4" />
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
};
